import { useState } from "react";
import { styles } from "../../../utils/styles/products-maintenance-styles";
import { useI18n } from "@/i18n/I18nProvider";
import { useFormik } from "formik";
import Box from "@mui/material/Box";
import dynamic from "next/dynamic";
import ProductMain from "@/components/products-revamp/product-main";
import { colors } from "@/utils/const";
import DynamicFAQSection from "@/components/DynamicFAQSection";
import StepsRow from "@/components/steps-row";
import ApplyProduct from "@/components/products-revamp/product-apply";
import CalcPopup from "@/components/products-revamp/CalcPopup";
import PgWithImgSection from "@/components/products-revamp/pg-with-img-section";
import Features from "@/components/products-revamp/features-section";
import { emptyProps, getProductPageData } from "@/services/get-product-data";
import {
  getLocalizedValue,
  getStaticTextByKey,
} from "@/utils/general-functions";
import { useSplitContentSections } from "@/hooks/product";
import { useProductLoanPackages } from "@/hooks/useProductLoanPackages";
import { getProductsText } from "../../../../locales/products";
const PageHead = dynamic(() => import("@/components/PageHead"));

export default function Index({
  heroData,
  contentWithImageData,
  faqData,
  stepsData,
  staticTextData,
  featuresData,
  mainData,
}) {
  const { t, locale } = useI18n();
  const products = getProductsText(locale);
  const onSubmit = async (values, actions) => {
    await new Promise((resolve) => setTimeout(resolve, 5000));
    actions.resetForm();
  };
  const {
    values,
    errors,
    touched,
    isSubmitting,
    handleBlur,
    handleChange,
    handleSubmit,
  } = useFormik({
    initialValues: {
      email: "",
      phoneNumber: "",
      governorate: "",
      area: "",
      username: "",
      lastname: "",
      car_type: "",
      car_price: "",
    },
    onSubmit,
  });
  const { targetProduct, data } = useProductLoanPackages(
    products.maintainance.id,
  );
  const [openCalc, setOpenCalc] = useState(false);
  const staticTexts = getStaticTextByKey(staticTextData?.data);
  const { fullNavySections, splitSections } =
    useSplitContentSections(contentWithImageData);
  return (
    <>
      <PageHead
        title={getLocalizedValue(mainData[0], "meta_title", locale)}
        description={getLocalizedValue(mainData[0], "meta_description", locale)}
        scripts={getLocalizedValue(mainData[0], "schema", locale)}
      />

      <Box sx={{ ...styles.container, overflow: "hidden" }}>
        {heroData?.length > 0 && (
          <ProductMain
            heroData={heroData}
            staticTexts={staticTexts}
            onApplyClick={() => {
              const formSection = document.getElementById("apply-form");
              if (formSection) {
                formSection.scrollIntoView({ behavior: "smooth" });
              }
            }}
            onCalcClick={() => setOpenCalc((prev) => !prev)}
            onExploreMoreClick={() => {
              const formSection = document.getElementById(
                "maintainance-features",
              );
              if (formSection) {
                formSection.scrollIntoView({ behavior: "smooth" });
              }
            }}
            imgStyle={{ mt: locale === "ar" ? 3 : { xs: 5, xl: "auto" } }}
            headerH1={true}
          />
        )}
        {fullNavySections?.length > 0 &&
          fullNavySections.map((section, index) => (
            <PgWithImgSection
              key={section.id || index}
              img={section.img}
              alt={section.alt_img}
              title={section.title?.text?.[locale]}
              titleType={section.title?.type}
              paragraph={section.description?.text?.[locale]}
              paragraphType={section.description?.type}
            />
          ))}
        <Box sx={{ position: "relative", bgcolor: colors.white }}>
          {featuresData?.length > 0 &&
            featuresData.map((section, index) => (
              <Features
                key={section?.id || index}
                section={section}
                bgHeight={{ xs: "72%", md: "46%", lg: "45%" }}
                id="maintainance-features"
              />
            ))}
          {/**faq section */}
          {faqData?.length > 0 &&
            faqData.map((section, index) => (
              <DynamicFAQSection
                key={section?.id || index}
                title={
                  staticTexts?.faqs_section_title?.value?.text[locale] || ""
                }
                items={section?.faqs}
                renderHTML={true}
                variantTitle="h2"
                variantQ="h3"
              />
            ))}
        </Box>
        {/**steps section */}
        {stepsData?.length > 0 &&
          stepsData.map((section, index) => (
            <StepsRow
              key={section.id || index}
              title={section?.title?.text?.[locale]}
              titleType={section?.title?.type}
              subtitle={section?.description?.text?.[locale]}
              subtitleType={section?.description?.type}
              steps={section?.items}
            />
          ))}

        <ApplyProduct
          img={heroData[0]?.img}
          title={mainData[0]?.form_title.text[locale]}
          titleType={mainData[0]?.form_title?.type}
          description={mainData[0]?.form_desc.text[locale]}
          descType={mainData[0]?.form_desc?.type}
          formType="b"
          product="22"
          source={46}
        />

        {targetProduct?.hasPlan && (
          <CalcPopup
            open={openCalc}
            onClose={() => setOpenCalc(false)}
            data={data}
            max_amount={targetProduct?.maxLimit}
            price_label={products.maintainance.price}
          />
        )}
      </Box>
    </>
  );
}

export async function getServerSideProps() {
  try {
    const data = await getProductPageData("maintenance");
    return { props: data };
  } catch {
    return { props: emptyProps };
  }
}
